import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MagneticButton } from "../MagneticButton";
import { postJSON, patchJSON, getJSON } from "../../lib/api";

const CATEGORIES = [
    { id: "firewall", label: "Firewall" },
    { id: "switch", label: "Switch" },
    { id: "access_point", label: "Access Point" },
    { id: "router", label: "Router" },
    { id: "server", label: "Server" },
    { id: "nas", label: "NAS" },
    { id: "ups", label: "UPS" },
    { id: "printer", label: "Printer" },
    { id: "endpoint", label: "Endpoint" },
    { id: "phone", label: "Phone" },
    { id: "camera", label: "Camera" },
    { id: "iot", label: "IoT" },
    { id: "other", label: "Other" },
];

/** Normalize a freely-typed MAC into AA:BB:CC:DD:EE:FF for live preview.
 *  Mirrors the backend's `_normalize_mac` exactly. Returns
 *  `{ valid, normalized }`. An empty input is treated as "not provided" —
 *  not invalid — so the caller can decide. */
function normalizeMac(raw) {
    if (raw === null || raw === undefined) return { valid: false, normalized: "" };
    const hex = String(raw).replace(/[\s:\-.]/g, "").toUpperCase();
    if (hex.length === 0) return { valid: false, normalized: "" };
    if (!/^[0-9A-F]{12}$/.test(hex)) return { valid: false, normalized: "" };
    return { valid: true, normalized: hex.match(/.{2}/g).join(":") };
}

function ipInCidr(ip, cidr) {
    if (!ip || !cidr) return true;
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/.exec(cidr);
    const i = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ip);
    if (!m || !i) return true; // let backend decide
    const toInt = (a, b, c, d) => ((+a << 24) | (+b << 16) | (+c << 8) | +d) >>> 0;
    const prefix = +m[5];
    const mask = prefix === 0 ? 0 : ((~0 << (32 - prefix)) >>> 0);
    const net = toInt(m[1], m[2], m[3], m[4]) & mask;
    const target = toInt(i[1], i[2], i[3], i[4]) & mask;
    return net === target;
}

// Module-level cache for the hardware catalog. The backend already sends
// `Cache-Control: public, max-age=3600`, but caching the parsed JSON in-memory
// avoids re-parsing every time the modal mounts within the same session.
let _catalogPromise = null;
function loadCatalog() {
    if (!_catalogPromise) {
        _catalogPromise = getJSON("/ipam/hardware-catalog").catch((err) => {
            // Don't poison the cache on transient errors — let the next mount retry.
            _catalogPromise = null;
            throw err;
        });
    }
    return _catalogPromise;
}

/** Isolated MAC field with local state, so each keystroke does NOT re-render
 *  the entire HardwareForm tree. The parent reads the current value through
 *  the imperative handle (`macFieldRef.current.getValue()`) at submit time. */
const MacField = React.forwardRef(({ initial = "" }, ref) => {
    const [value, setValue] = React.useState(initial);
    const preview = React.useMemo(() => normalizeMac(value), [value]);
    const empty = !value || !value.trim();

    React.useImperativeHandle(ref, () => ({
        getValue: () => value,
        getCanonical: () => (preview.valid ? preview.normalized : value),
    }), [value, preview]);

    const onChange = (e) => setValue(e.target.value);
    const onBlur = (e) => {
        const r = normalizeMac(e.currentTarget.value);
        if (r.valid && r.normalized !== e.currentTarget.value) {
            setValue(r.normalized);
        }
    };

    const invalid = !empty && !preview.valid;
    return (
        <>
            <input
                type="text"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="aa:bb:cc:dd:ee:ff or aabbccddeeff"
                className={`w-full px-3 py-2 rounded-md bg-elevated/60 border text-text-primary text-[12.5px] font-mono focus:outline-none ${
                    invalid
                        ? "border-accent-amber/40 focus:border-accent-amber/60"
                        : "border-white/[0.06] focus:border-accent-cyan/40"
                }`}
                data-testid="hw-field-mac"
            />
            {!empty && (
                <p
                    className={`mt-1 text-[10.5px] font-mono ${preview.valid ? "text-accent-mint" : "text-accent-amber"}`}
                    data-testid="hw-field-mac-preview"
                >
                    {preview.valid ? `→ ${preview.normalized}` : "Not a valid MAC (12 hex digits expected)"}
                </p>
            )}
        </>
    );
});
MacField.displayName = "MacField";

// Defined at module scope so it doesn't lose identity across renders. When
// `Section` was defined inside `HardwareForm`, every keystroke produced a
// fresh function ref, which made React unmount/remount the entire section
// subtree — wiping the focus and losing the input's blur listener.
const Section = ({ id, label, expanded, setExpanded, children }) => (
    <div className="rounded-lg border border-white/[0.05] bg-elevated/30 overflow-hidden">
        <button
            type="button"
            onClick={() => setExpanded((s) => ({ ...s, [id]: !s[id] }))}
            className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-mono uppercase tracking-[0.18em] text-text-muted hover:text-text-primary transition-colors"
            data-testid={`hw-section-toggle-${id}`}
        >
            <span>{label}</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${expanded[id] ? "rotate-180" : ""}`} />
        </button>
        {expanded[id] && <div className="px-3 pb-3 grid gap-3 md:grid-cols-2">{children}</div>}
    </div>
);

export const HardwareForm = ({ client, subnets, asset, onClose, onSaved }) => {
    const isEdit = Boolean(asset);
    const [form, setForm] = useState({
        category: asset?.category || "firewall",
        vendor: asset?.vendor || "",
        model: asset?.model || "",
        cpe: asset?.cpe || "",
        hostname: asset?.hostname || "",
        serial_number: asset?.serial_number || "",
        ip_address: asset?.ip_address || "",
        subnet_id: asset?.subnet_id || "",
        firmware_version: asset?.firmware_version || "",
        purchase_date: asset?.purchase_date || "",
        warranty_expiry: asset?.warranty_expiry || "",
        location: asset?.location || "",
        notes: asset?.notes || "",
        tags: (asset?.tags || []).join(", "),
        status: asset?.status || "active",
    });
    const [expanded, setExpanded] = useState({ network: false, details: false, notes: false });
    const [saving, setSaving] = useState(false);
    const [catalog, setCatalog] = useState({ vendors: [] });
    // Tracks whether the user has explicitly edited the CPE input — used to
    // decide whether the catalog auto-fill should overwrite it on (vendor,
    // model) change. Edits = leave it alone.
    const cpeTouched = useRef(Boolean(asset?.cpe));
    // Isolated MAC field: lives in its own component with its own state so
    // that typing into MAC never re-renders the parent form (which prevented
    // a controlled-input race that dropped keystrokes under React StrictMode).
    const macFieldRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        loadCatalog()
            .then((data) => { if (!cancelled) setCatalog(data || { vendors: [] }); })
            .catch(() => { /* silent — form still works with free-text vendor/model */ });
        return () => { cancelled = true; };
    }, []);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Filter catalog vendors by selected category for cleaner UX.
    const catalogVendors = useMemo(() => {
        return (catalog.vendors || []).filter((v) =>
            !v.category_default || v.category_default === form.category,
        );
    }, [catalog, form.category]);

    const catalogModels = useMemo(() => {
        const v = (catalog.vendors || []).find((x) => x.name === form.vendor);
        return v ? v.models : [];
    }, [catalog, form.vendor]);

    // When user picks a (vendor, model) pair from the catalog, auto-populate CPE
    // — unless they've already typed/edited one (cpeTouched). The backend will
    // do the same lookup at write-time, but doing it here gives instant feedback.
    useEffect(() => {
        if (!form.vendor || !form.model) return;
        if (cpeTouched.current) return;
        const v = (catalog.vendors || []).find((x) => x.name.toLowerCase() === form.vendor.toLowerCase());
        const m = v?.models.find((x) => x.name.toLowerCase() === form.model.toLowerCase());
        if (m?.cpe && m.cpe !== form.cpe) {
            setForm((f) => ({ ...f, cpe: m.cpe }));
        }
    }, [form.vendor, form.model, catalog]);  // eslint-disable-line

    const linkedSubnet = useMemo(
        () => subnets.find((s) => s.id === form.subnet_id) || null,
        [subnets, form.subnet_id],
    );
    const ipOutsideSubnet = linkedSubnet && form.ip_address && !ipInCidr(form.ip_address, linkedSubnet.cidr);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.category) { toast.error("Pick a category"); return; }
        if (!form.vendor.trim()) { toast.error("Vendor is required"); return; }
        if (!form.model.trim()) { toast.error("Model is required"); return; }

        // Read MAC from the isolated field. The field has already canonicalized
        // on blur; we ask for the canonical view to cover the case where the
        // user clicks Submit without leaving the input.
        const macRaw = (macFieldRef.current?.getValue() || "").trim();
        const macResult = normalizeMac(macRaw);
        if (macRaw && !macResult.valid) { toast.error("Invalid MAC address"); return; }
        const macForPayload = macRaw ? macResult.normalized : null;

        if (ipOutsideSubnet) { toast.error(`IP ${form.ip_address} is outside ${linkedSubnet.cidr}`); return; }

        const payload = {
            category: form.category,
            vendor: form.vendor.trim(),
            model: form.model.trim(),
            hostname: form.hostname.trim() || null,
            serial_number: form.serial_number.trim() || null,
            mac_address: macForPayload,
            ip_address: form.ip_address.trim() || null,
            subnet_id: form.subnet_id || null,
            firmware_version: form.firmware_version.trim() || null,
            purchase_date: form.purchase_date || null,
            warranty_expiry: form.warranty_expiry || null,
            location: form.location.trim() || null,
            notes: form.notes || null,
            tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
            status: form.status,
        };
        // Only include `cpe` in the payload if the user actively touched it.
        // Otherwise the backend will fill it in from the catalog (single source
        // of truth) — and we let it.
        if (cpeTouched.current) {
            payload.cpe = form.cpe.trim() || null;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await patchJSON(`/ipam/hardware/${asset.id}`, payload);
                toast.success("Asset updated");
            } else {
                await postJSON(`/ipam/clients/${client.id}/hardware`, payload);
                toast.success("Asset added");
            }
            onSaved();
        } catch (err) {
            if (err.status === 422 && err.payload?.detail) {
                toast.error(typeof err.payload.detail === "string" ? err.payload.detail : "Validation failed");
            } else {
                toast.error(err.message || "Save failed");
            }
            setSaving(false);
        }
    };

    const Section = ({ id, label, children }) => (
        <div className="rounded-lg border border-white/[0.05] bg-elevated/30 overflow-hidden">
            <button
                type="button"
                onClick={() => setExpanded((s) => ({ ...s, [id]: !s[id] }))}
                className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-mono uppercase tracking-[0.18em] text-text-muted hover:text-text-primary transition-colors"
                data-testid={`hw-section-toggle-${id}`}
            >
                <span>{label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${expanded[id] ? "rotate-180" : ""}`} />
            </button>
            {expanded[id] && <div className="px-3 pb-3 grid gap-3 md:grid-cols-2">{children}</div>}
        </div>
    );

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-canvas/80 backdrop-blur-md"
            data-testid="hw-form-modal"
            role="dialog"
            aria-modal="true"
        >
            <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 cursor-default" />
            <form
                onSubmit={submit}
                className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/[0.08] bg-surface/95 shadow-heavy-popover p-6 md:p-8"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-text-dim hover:text-text-primary p-1.5 rounded"
                    aria-label="Close"
                    data-testid="hw-form-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                    {isEdit ? "Edit asset" : "Add asset"}
                </p>
                <h2 className="mt-2 font-display font-semibold text-2xl text-text-primary tracking-tight">
                    {isEdit ? `${asset.vendor} ${asset.model}` : "Add a hardware asset"}
                </h2>

                {/* Step 1 — Category (chips) */}
                <div className="mt-5">
                    <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">Category *</span>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="hw-categories">
                        {CATEGORIES.map((c) => (
                            <button
                                type="button"
                                key={c.id}
                                onClick={() => update("category", c.id)}
                                className={`px-2.5 py-1 rounded text-[11.5px] font-medium border transition-colors ${
                                    form.category === c.id
                                        ? "bg-accent-cyan/[0.12] border-accent-cyan/40 text-accent-cyan"
                                        : "border-white/[0.06] text-text-muted hover:border-accent-cyan/30"
                                }`}
                                data-testid={`hw-category-${c.id}`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Step 2 — Vendor / Model / CPE */}
                <div className="mt-5 grid gap-3 md:grid-cols-2">
                    <Field label="Vendor *">
                        <input
                            list="hw-vendor-list"
                            type="text"
                            value={form.vendor}
                            onChange={(e) => update("vendor", e.target.value)}
                            placeholder="Fortinet, Cisco, …"
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="hw-field-vendor"
                        />
                        <datalist id="hw-vendor-list">
                            {catalogVendors.map((v) => <option key={v.name} value={v.name} />)}
                        </datalist>
                    </Field>
                    <Field label="Model *">
                        <input
                            list="hw-model-list"
                            type="text"
                            value={form.model}
                            onChange={(e) => update("model", e.target.value)}
                            placeholder="FortiGate 60F, …"
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="hw-field-model"
                        />
                        <datalist id="hw-model-list">
                            {catalogModels.map((m) => <option key={m.name} value={m.name} />)}
                        </datalist>
                    </Field>
                    <div className="md:col-span-2">
                        <Field label="CPE (auto-filled from catalog when possible)">
                            <input
                                type="text"
                                value={form.cpe}
                                onChange={(e) => { cpeTouched.current = true; update("cpe", e.target.value); }}
                                placeholder="cpe:2.3:h:vendor:product:-:*:*:*:*:*:*:*"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[12px] font-mono focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-cpe"
                            />
                        </Field>
                        <p className="mt-1 text-[10.5px] text-text-dim">
                            This is the link to the future weekly CVE scan. Leave blank if the model isn't in the
                            catalog — the backend will try to match (vendor, model) for you.
                        </p>
                    </div>
                </div>

                {/* Step 3 — Network details */}
                <div className="mt-5 space-y-3">
                    <Section id="network" label="Network details" expanded={expanded} setExpanded={setExpanded}>
                        <Field label="Hostname">
                            <input
                                type="text"
                                value={form.hostname}
                                onChange={(e) => update("hostname", e.target.value)}
                                placeholder="fw-milano-01"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-hostname"
                            />
                        </Field>
                        <Field label="Linked subnet">
                            <select
                                value={form.subnet_id}
                                onChange={(e) => update("subnet_id", e.target.value)}
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-subnet"
                            >
                                <option value="">— none —</option>
                                {subnets.map((s) => (
                                    <option key={s.id} value={s.id}>{s.cidr} · {s.name}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="IP address">
                            <input
                                type="text"
                                value={form.ip_address}
                                onChange={(e) => update("ip_address", e.target.value)}
                                placeholder={linkedSubnet ? `${linkedSubnet.cidr} member` : "10.10.0.5"}
                                className={`w-full px-3 py-2 rounded-md bg-elevated/60 border text-text-primary text-[13px] font-mono focus:outline-none ${
                                    ipOutsideSubnet ? "border-accent-amber/40 focus:border-accent-amber/60" : "border-white/[0.06] focus:border-accent-cyan/40"
                                }`}
                                data-testid="hw-field-ip"
                            />
                            {linkedSubnet && (
                                <p className={`mt-1 text-[10.5px] ${ipOutsideSubnet ? "text-accent-amber" : "text-text-dim"}`}>
                                    Must be within {linkedSubnet.cidr}
                                </p>
                            )}
                        </Field>
                        <Field label="MAC address">
                            <MacField ref={macFieldRef} initial={asset?.mac_address || ""} />
                        </Field>
                    </Section>

                    {/* Step 4 — Asset details */}
                    <Section id="details" label="Asset details" expanded={expanded} setExpanded={setExpanded}>
                        <Field label="Serial number">
                            <input
                                type="text"
                                value={form.serial_number}
                                onChange={(e) => update("serial_number", e.target.value)}
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-serial"
                            />
                        </Field>
                        <Field label="Firmware version">
                            <input
                                type="text"
                                value={form.firmware_version}
                                onChange={(e) => update("firmware_version", e.target.value)}
                                placeholder="7.4.3"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-firmware"
                            />
                        </Field>
                        <Field label="Purchase date">
                            <input
                                type="date"
                                value={form.purchase_date}
                                onChange={(e) => update("purchase_date", e.target.value)}
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-purchase-date"
                            />
                        </Field>
                        <Field label="Warranty expiry">
                            <input
                                type="date"
                                value={form.warranty_expiry}
                                onChange={(e) => update("warranty_expiry", e.target.value)}
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-warranty"
                            />
                        </Field>
                        <Field label="Location">
                            <input
                                type="text"
                                value={form.location}
                                onChange={(e) => update("location", e.target.value)}
                                placeholder="Rack A1 — HQ Milano"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-location"
                            />
                        </Field>
                        <Field label="Tags (comma-separated)">
                            <input
                                type="text"
                                value={form.tags}
                                onChange={(e) => update("tags", e.target.value)}
                                placeholder="critical, perimeter"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-tags"
                            />
                        </Field>
                    </Section>

                    {/* Step 5 — Notes */}
                    <Section id="notes" label="Notes" expanded={expanded} setExpanded={setExpanded}>
                        <div className="md:col-span-2">
                            <textarea
                                rows={4}
                                value={form.notes}
                                onChange={(e) => update("notes", e.target.value)}
                                placeholder="Maintenance reminders, peculiarities, contracts…"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[12.5px] font-mono focus:outline-none focus:border-accent-cyan/40"
                                data-testid="hw-field-notes"
                            />
                        </div>
                    </Section>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md border border-white/[0.08] text-[13px] text-text-muted hover:text-text-primary"
                        data-testid="hw-form-cancel"
                    >
                        Cancel
                    </button>
                    <MagneticButton
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan disabled:opacity-40"
                        ariaLabel="Save asset"
                        data-testid="hw-form-submit"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        {saving ? "Saving…" : (isEdit ? "Save changes" : "Add asset")}
                    </MagneticButton>
                </div>
            </form>
        </div>
    );
};

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">{label}</span>
        <div className="mt-1">{children}</div>
    </label>
);

export default HardwareForm;
